import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { useCompaniesPaged, useCompanyMutations, type CompanyInput, type SiteInput } from '../api/admin'
import type { CompanyDto, SiteDto } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { Badge, Empty, Modal } from '../components/ui'
import { errMsg } from './TemplatesPage'

const TYPES: CompanyDto['type'][] = ['Customer', 'Supplier', 'Self']
const blankCompany = (): CompanyInput => ({ type: 'Customer', name: '', code: '', taxNumber: '', address: '', city: '', contactName: '', contactEmail: '', contactPhone: '', employeeCount: null, sector: '' })
const blankSite = (): SiteInput => ({ name: '', code: '', address: '', city: '', employeeCount: null })

export default function CompaniesPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const set = (k: string, v: string) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); if (k !== 'page') n.delete('page'); setParams(n) }
  const filters = { page: Number(params.get('page') ?? 1), search: params.get('search') ?? '', type: params.get('type') ?? '', includeInactive: params.get('inactive') === '1' }
  const companies = useCompaniesPaged(filters)
  const m = useCompanyMutations()
  const canManage = user?.roles.some((r) => r.role === 'TenantAdmin' || r.role === 'SuperAdmin') ?? false
  const [edit, setEdit] = useState<{ id?: string; input: CompanyInput; isActive: boolean; sites: SiteDto[] } | null>(null)
  const [site, setSite] = useState<{ companyId: string; id?: string; input: SiteInput; isActive: boolean } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const openEdit = (c: CompanyDto) => setEdit({ id: c.id, isActive: c.isActive, sites: c.sites, input: { type: c.type, name: c.name, code: c.code ?? '', address: c.address ?? '', city: c.city ?? '', employeeCount: c.employeeCount ?? null, taxNumber: '', contactName: '', contactEmail: '', contactPhone: '', sector: '' } })
  const saveCompany = async () => {
    if (!edit) return
    setError(null)
    const input = { ...edit.input, code: edit.input.code || undefined, taxNumber: edit.input.taxNumber || undefined, address: edit.input.address || undefined, city: edit.input.city || undefined, contactName: edit.input.contactName || undefined, contactEmail: edit.input.contactEmail || undefined, contactPhone: edit.input.contactPhone || undefined, sector: edit.input.sector || undefined }
    try {
      if (edit.id) await m.update.mutateAsync({ id: edit.id, input, isActive: edit.isActive })
      else await m.create.mutateAsync(input)
      setEdit(null)
    } catch (e) {
      setError(errMsg(e))
    }
  }
  const saveSite = async () => {
    if (!site) return
    setError(null)
    const input = { ...site.input, code: site.input.code || undefined, address: site.input.address || undefined, city: site.input.city || undefined }
    try {
      if (site.id) await m.updateSite.mutateAsync({ companyId: site.companyId, siteId: site.id, input, isActive: site.isActive })
      else await m.createSite.mutateAsync({ companyId: site.companyId, input })
      setSite(null)
      setEdit(null)
    } catch (e) {
      setError(errMsg(e))
    }
  }

  return (
    <div className="stack">
      <div className="row between wrap">
        <h2>{t('companies.title')}</h2>
        {canManage ? <button onClick={() => { setError(null); setEdit({ input: blankCompany(), isActive: true, sites: [] }) }}>{t('companies.new')}</button> : null}
      </div>
      <div className="card filters">
        <input placeholder={t('common.search')} value={filters.search} onChange={(e) => set('search', e.target.value)} />
        <select value={filters.type} onChange={(e) => set('type', e.target.value)}>
          <option value="">{t('companies.allTypes')}</option>
          {TYPES.map((x) => <option key={x} value={x}>{t(`companyType.${x}`)}</option>)}
        </select>
        <label className="row"><input type="checkbox" checked={filters.includeInactive} onChange={(e) => set('inactive', e.target.checked ? '1' : '')} /> {t('companies.includeInactive')}</label>
      </div>
      <div className="card">
        {companies.isLoading ? <p>{t('common.loading')}</p> : !companies.data?.items.length ? <Empty text={t('companies.empty')} /> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>{t('companies.name')}</th><th>{t('companies.type')}</th><th>{t('companies.city')}</th><th className="num">{t('companies.employees')}</th><th>{t('companies.sites')}</th><th /></tr></thead>
              <tbody>
                {companies.data.items.map((c) => (
                  <tr key={c.id}>
                    <td><b>{c.name}</b>{c.code ? <span className="muted small"> · {c.code}</span> : null}{!c.isActive ? <Badge color="#e34948">{t('users.inactive')}</Badge> : null}{c.address ? <div className="muted small">{c.address}</div> : null}</td>
                    <td><Badge>{t(`companyType.${c.type}`)}</Badge></td>
                    <td>{c.city}</td>
                    <td className="num">{c.employeeCount ?? '–'}</td>
                    <td className="small">{c.sites.map((s) => s.name).join(', ') || '–'}</td>
                    <td className="row" style={{ whiteSpace: 'nowrap' }}>
                      <Link to={`/reports?companyId=${c.id}`}>{t('nav.reports')}</Link>
                      {canManage ? <button className="ghost" onClick={() => { setError(null); openEdit(c) }}>{t('companies.edit')}</button> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {companies.data && companies.data.totalPages > 1 ? (
          <div className="row between" style={{ marginTop: 8 }}>
            <button className="secondary small-btn" disabled={filters.page <= 1} onClick={() => set('page', String(filters.page - 1))}>‹</button>
            <span className="muted small">{t('common.pageOf', { page: companies.data.page, pages: companies.data.totalPages, total: companies.data.totalCount })}</span>
            <button className="secondary small-btn" disabled={filters.page >= companies.data.totalPages} onClick={() => set('page', String(filters.page + 1))}>›</button>
          </div>
        ) : null}
      </div>

      {edit ? (
        <Modal title={edit.id ? t('companies.edit') : t('companies.new')} onClose={() => setEdit(null)} width={720}>
          <div className="grid two">
            <label>{t('companies.name')}<input value={edit.input.name} onChange={(e) => setEdit({ ...edit, input: { ...edit.input, name: e.target.value } })} /></label>
            <label>{t('companies.type')}
              <select value={edit.input.type} onChange={(e) => setEdit({ ...edit, input: { ...edit.input, type: e.target.value as CompanyDto['type'] } })}>{TYPES.map((x) => <option key={x} value={x}>{t(`companyType.${x}`)}</option>)}</select>
            </label>
            <label>{t('companies.code')}<input value={edit.input.code ?? ''} onChange={(e) => setEdit({ ...edit, input: { ...edit.input, code: e.target.value } })} /></label>
            <label>{t('companies.taxNumber')}<input value={edit.input.taxNumber ?? ''} onChange={(e) => setEdit({ ...edit, input: { ...edit.input, taxNumber: e.target.value } })} /></label>
            <label>{t('companies.city')}<input value={edit.input.city ?? ''} onChange={(e) => setEdit({ ...edit, input: { ...edit.input, city: e.target.value } })} /></label>
            <label>{t('companies.employees')}<input type="number" value={edit.input.employeeCount ?? ''} onChange={(e) => setEdit({ ...edit, input: { ...edit.input, employeeCount: e.target.value ? Number(e.target.value) : null } })} /></label>
            <label>{t('companies.sector')}<input value={edit.input.sector ?? ''} onChange={(e) => setEdit({ ...edit, input: { ...edit.input, sector: e.target.value } })} /></label>
            <label>{t('companies.contactName')}<input value={edit.input.contactName ?? ''} onChange={(e) => setEdit({ ...edit, input: { ...edit.input, contactName: e.target.value } })} /></label>
            <label>{t('companies.contactEmail')}<input value={edit.input.contactEmail ?? ''} onChange={(e) => setEdit({ ...edit, input: { ...edit.input, contactEmail: e.target.value } })} /></label>
            <label>{t('companies.contactPhone')}<input value={edit.input.contactPhone ?? ''} onChange={(e) => setEdit({ ...edit, input: { ...edit.input, contactPhone: e.target.value } })} /></label>
          </div>
          <label>{t('companies.address')}<textarea rows={2} value={edit.input.address ?? ''} onChange={(e) => setEdit({ ...edit, input: { ...edit.input, address: e.target.value } })} /></label>
          {edit.id ? <label className="row"><input type="checkbox" checked={edit.isActive} onChange={(e) => setEdit({ ...edit, isActive: e.target.checked })} /> {t('users.active')}</label> : null}
          {edit.id ? (
            <>
              <div className="row between"><h3 style={{ margin: 0 }}>{t('companies.sites')}</h3><button className="secondary small-btn" onClick={() => setSite({ companyId: edit.id!, input: blankSite(), isActive: true })}>{t('companies.newSite')}</button></div>
              {edit.sites.length === 0 ? <p className="muted small">{t('companies.noSites')}</p> : (
                <table className="table compact">
                  <tbody>
                    {edit.sites.map((s) => (
                      <tr key={s.id}>
                        <td><b>{s.name}</b>{s.code ? <span className="muted small"> · {s.code}</span> : null}{!s.isActive ? <Badge color="#e34948">{t('users.inactive')}</Badge> : null}</td>
                        <td className="small">{s.city}</td>
                        <td className="num">{s.employeeCount ?? '–'}</td>
                        <td><button className="ghost" onClick={() => setSite({ companyId: edit.id!, id: s.id, isActive: s.isActive, input: { name: s.name, code: s.code ?? '', address: s.address ?? '', city: s.city ?? '', employeeCount: s.employeeCount ?? null } })}>{t('companies.edit')}</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          ) : <p className="muted small">{t('companies.sitesAfterSave')}</p>}
          {error ? <p className="error">{error}</p> : null}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="secondary" onClick={() => setEdit(null)}>{t('common.cancel')}</button>
            <button disabled={!edit.input.name.trim() || m.create.isPending || m.update.isPending} onClick={() => void saveCompany()}>{t('common.save')}</button>
          </div>
        </Modal>
      ) : null}

      {site ? (
        <Modal title={site.id ? t('companies.editSite') : t('companies.newSite')} onClose={() => setSite(null)}>
          <label>{t('companies.name')}<input value={site.input.name} onChange={(e) => setSite({ ...site, input: { ...site.input, name: e.target.value } })} /></label>
          <div className="grid two">
            <label>{t('companies.code')}<input value={site.input.code ?? ''} onChange={(e) => setSite({ ...site, input: { ...site.input, code: e.target.value } })} /></label>
            <label>{t('companies.city')}<input value={site.input.city ?? ''} onChange={(e) => setSite({ ...site, input: { ...site.input, city: e.target.value } })} /></label>
            <label>{t('companies.employees')}<input type="number" value={site.input.employeeCount ?? ''} onChange={(e) => setSite({ ...site, input: { ...site.input, employeeCount: e.target.value ? Number(e.target.value) : null } })} /></label>
          </div>
          <label>{t('companies.address')}<textarea rows={2} value={site.input.address ?? ''} onChange={(e) => setSite({ ...site, input: { ...site.input, address: e.target.value } })} /></label>
          {site.id ? <label className="row"><input type="checkbox" checked={site.isActive} onChange={(e) => setSite({ ...site, isActive: e.target.checked })} /> {t('users.active')}</label> : null}
          {error ? <p className="error">{error}</p> : null}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="secondary" onClick={() => setSite(null)}>{t('common.cancel')}</button>
            <button disabled={!site.input.name.trim()} onClick={() => void saveSite()}>{t('common.save')}</button>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}
